import md5 from 'md5';

import { Locale } from '@suntex.fim/enums';

export * from './gym';
export * from './skillSet';

const INDIA_SEASON_START_MONTH = 6;
const US_SEASON_START_MONTH = 8;
const YEAR_DIGITS = 4;

export function resolveLocale(hostname: string, region?: string): Locale {
  if (hostname.endsWith('.in')) return Locale.IN;
  if (hostname.endsWith('.com')) return Locale.US;
  return region === 'IN' ? Locale.IN : Locale.US;
}

export function createLocalization(locale: Locale) {
  const isIndia = locale === Locale.IN;
  const countryCode = isIndia ? 'in' : 'us';
  const dateFormat = isIndia ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
  const seasonStartMonth = isIndia
    ? INDIA_SEASON_START_MONTH
    : US_SEASON_START_MONTH;
  const words: Record<string, string> = {
    math: 'maths',
    grade: 'standard',
    grades: 'standards',
    gr: 'std',
  };
  function pad(value: number) {
    return String(value).padStart(2, '0');
  }
  function text(value: string): string {
    return isIndia
      ? value
          .split(/(First In Math)/gi)
          .map((part) =>
            /^First In Math$/i.test(part)
              ? part
              : part.replace(/\b(math|grades?|gr)\b/gi, (word) => {
                  const replacement = words[word.toLowerCase()]!;
                  return word === word.toUpperCase()
                    ? replacement.toUpperCase()
                    : word[0] === word[0]!.toUpperCase()
                      ? replacement[0]!.toUpperCase() + replacement.slice(1)
                      : replacement;
                }),
          )
          .join('')
      : value;
  }
  function formatDate(date: Date): string {
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    return `${isIndia ? `${day}/${month}` : `${month}/${day}`}/${String(date.getFullYear()).padStart(YEAR_DIGITS, '0')}`;
  }
  function formatTime(date: Date): string {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  return {
    locale,
    countryCode,
    suffix: isIndia ? '-in' : '',
    dateFormat,
    dateFormatDisplay: dateFormat.toLowerCase(),
    seasonStartMonth,
    text,
    formatDate,
    formatTime,
    formatDateTime: (date: Date) => `${formatDate(date)} ${formatTime(date)}`,
    parseDate(value: string): Date {
      const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
      if (!match) throw new RangeError(`Expected ${dateFormat}`);
      const [, first, second, yearString] = match;
      const day = Number(isIndia ? first : second);
      const month = Number(isIndia ? second : first);
      const year = Number(yearString);
      const date = new Date(0);
      date.setFullYear(year, month - 1, day);
      date.setHours(0, 0, 0, 0);
      if (
        year < 1 ||
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        throw new RangeError(`Invalid date: ${value}`);
      }
      return date;
    },
    fileName(value: string): string {
      if (!isIndia) return value;
      const normalized = value.replace(/\\/g, '/');
      const extension = normalized.lastIndexOf('.');
      return extension > normalized.lastIndexOf('/')
        ? `${normalized.slice(0, extension)}-in${normalized.slice(extension)}`
        : `${normalized}-in`;
    },
    localize(value: string): string {
      return value.replace(/\{(\w+)\}/g, (match, name: string) => {
        if (name.toLowerCase() !== 'cc') {
          throw new Error(`Unhandled match ${match}`);
        }
        return countryCode;
      });
    },
    rootUrl(value: string): string {
      const url = new URL(value);
      if (/^(?:[\w-]+\.)?firstinmath\.(?:com|in)$/.test(url.hostname)) {
        url.hostname = url.hostname.replace(
          /\.(com|in)$/,
          `.${isIndia ? 'in' : 'com'}`,
        );
      }
      return url.origin;
    },
    yearStart(now: Date): Date {
      const year = Number(currentFimYear({ now, isIndia }).split('/')[0]);
      return new Date(year, seasonStartMonth - 1, 1);
    },
    yearEnd(now: Date): Date {
      const year = Number(currentFimYear({ now, isIndia }).split('/')[1]);
      return new Date(year, seasonStartMonth - 1, 0);
    },
  };
}

export interface UpdateRequest {
  hash: string;
  playerId: number;
  salt: string;
  [key: string]: unknown;
}

/**
 * Populates the `salt` and `hash` fields of an update request using an HMAC-style
 * MD5 checksum. The hash is computed over all non-salt, non-hash property values
 * (sorted by key, lowercased) prepended by the salt.
 *
 * @param request - The request object to mutate in place.
 * @param salt - The salt value (provided by the caller from `window.salter()` or equivalent).
 */
export function hashUpdateRequest(request: UpdateRequest, salt: string): void {
  request.salt = salt;

  const excluded = new Set<string>(['salt', 'hash']);

  const hashSource =
    salt +
    Object.keys(request)
      .filter((key) => !excluded.has(key))
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .map((key) => request[key])
      .filter((val) => val !== null && val !== undefined)
      .map((val) => String(val as string | number | boolean).toLowerCase())
      .join('');

  request.hash = md5(hashSource);
}

export function currentFimYear({
  now,
  isIndia,
}: {
  now: Date;
  isIndia?: boolean;
}): string {
  //the current year
  const year = currentFimYearNumber({ now, isIndia });

  //format and return
  return `${year}/${year + 1}`;
}

export function previousFimYear({
  now,
  isIndia,
}: {
  now: Date;
  isIndia?: boolean;
}): string {
  //the previous year
  const year = currentFimYearNumber({ now, isIndia }) - 1;

  //format and return
  return `${year}/${year + 1}`;
}

function currentFimYearNumber({
  now,
  isIndia,
}: {
  now: Date;
  isIndia?: boolean;
}): number {
  //the year is this
  const year = now.getFullYear();

  //js months start at zero, so:
  const month = now.getMonth() + 1;

  //when does the year roll over; june in india, august in the US
  const rollover = isIndia ? INDIA_SEASON_START_MONTH : US_SEASON_START_MONTH;

  //if current month >= rollover month, the fim year number is this one, otherwise the previous one
  //this is the difference between 2024/2025 and 2023/2024
  return month >= rollover ? year : year - 1;
}
